import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { canOpenSection } from '../lib/permissions';
import { getAllUsersWithToken } from '../api/services/usersService';
import { getAllVehicles } from '../api/services/vehiclesService';
import { Avatar, VehicleThumb, LineIcon, ICONS } from './ui/dg';

/**
 * Recherche de l'en-tête (⌘K) : une section, une personne ou un véhicule.
 * Les personnes et les véhicules sont lus une fois puis filtrés sur place,
 * pas à chaque frappe. Un résultat ouvre la section, déjà filtrée sur lui.
 */

const SECTIONS: { id: string; label: string; keywords: string }[] = [
  { id: 'dashboard', label: 'Tableau de bord', keywords: 'accueil tableau bord' },
  { id: 'mytrips', label: 'Trajets', keywords: 'trajets locations réservations' },
  { id: 'myrentals', label: 'Flotte', keywords: 'flotte véhicules annonces' },
  { id: 'verifications', label: 'Vérifications', keywords: 'vérifications permis documents' },
  { id: 'support', label: 'Support', keywords: 'support tickets discussions aide' },
  { id: 'users', label: 'Utilisateurs', keywords: 'utilisateurs comptes personnes' },
  { id: 'analytics', label: 'Analytics', keywords: 'analytics statistiques' },
  { id: 'frais', label: 'Frais', keywords: 'frais commission règlements' },
  { id: 'promo-codes', label: 'Codes promo', keywords: 'codes promo remise réduction propriétaires' },
  { id: 'communication', label: 'Communication', keywords: 'communication campagnes push e-mails' },
  { id: 'journaux', label: 'Journaux', keywords: 'journaux logs api historique' },
  { id: 'settings', label: 'Paramètres', keywords: 'paramètres réglages' },
  { id: 'user', label: 'Mon profil', keywords: 'profil compte' },
];

type Result =
  | { kind: 'section'; id: string; label: string }
  | { kind: 'user'; section: 'users'; label: string; sub: string; query: string; photo?: string | null }
  | { kind: 'vehicle'; section: 'myrentals'; label: string; sub: string; query: string; vehicle: any };

const GlobalSearch: React.FC = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const isStaff = user?.role === 'admin' || user?.role === 'moderator';
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [users, setUsers] = useState<any[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const loaded = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  // Chargées au premier focus : rien n'est lu tant qu'on ne cherche pas.
  const ensureLoaded = useCallback(() => {
    if (loaded.current || !isStaff || !token) return;
    loaded.current = true;
    getAllUsersWithToken(token).then((d: any) => setUsers(Array.isArray(d) ? d : [])).catch(() => {});
    getAllVehicles(token).then((d: any) => setVehicles(Array.isArray(d) ? d : [])).catch(() => {});
  }, [isStaff, token]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const results: Result[] = [];
  if (q) {
    if (isStaff && q.length >= 2) {
      users
        .filter((u) => `${u.first_name || ''} ${u.last_name || ''} ${u.email || ''}`.toLowerCase().includes(q))
        .slice(0, 5)
        .forEach((u) => {
          const label = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
          results.push({ kind: 'user', section: 'users', label, sub: u.email || 'Utilisateur', query: label, photo: u.profile_photo });
        });
      vehicles
        .filter((v) => `${v.brand || ''} ${v.model || ''} ${v.serial_number || ''}`.toLowerCase().includes(q))
        .slice(0, 5)
        .forEach((v) => {
          const label = [v.brand, v.model].filter(Boolean).join(' ') || v.serial_number || 'Véhicule';
          results.push({ kind: 'vehicle', section: 'myrentals', label, sub: v.serial_number || 'Véhicule', query: label, vehicle: v });
        });
    }
    SECTIONS
      .filter((s) => canOpenSection(user?.role, s.id) && `${s.label} ${s.keywords}`.toLowerCase().includes(q))
      .forEach((s) => results.push({ kind: 'section', id: s.id, label: s.label }));
  }

  const select = (r: Result) => {
    if (r.kind === 'section') navigate(`/${r.id}`);
    else navigate(`/${r.section}?q=${encodeURIComponent(r.query)}`);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); return; }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => (i + 1) % results.length); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => (i - 1 + results.length) % results.length); }
    if (e.key === 'Enter') { e.preventDefault(); select(results[Math.min(index, results.length - 1)]); }
  };

  return (
    <div ref={boxRef} className="relative hidden w-[300px] md:block">
      <LineIcon d={ICONS.search} size={16} color="#9CA3AF" className="pointer-events-none absolute left-[14px] top-1/2 -translate-y-1/2" />
      <input
        ref={inputRef}
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setIndex(0); }}
        onFocus={() => { ensureLoaded(); setOpen(true); }}
        onKeyDown={onKeyDown}
        placeholder="Rechercher un trajet, une personne…"
        aria-label="Recherche"
        className="h-10 w-full rounded-full border border-[#E5E7EB] bg-white pl-10 pr-4 text-[13.5px] text-[#1E293B] placeholder-[#9CA3AF] focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10"
      />
      {open && results.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-50 mt-2 max-h-96 overflow-y-auto rounded-[18px] border border-[#EEF0F4] bg-white py-1.5 shadow-[0_10px_30px_rgba(15,23,42,0.15)]">
          {results.map((r, i) => (
            <button key={`${r.kind}-${r.label}-${i}`} type="button" onMouseEnter={() => setIndex(i)} onClick={() => select(r)}
              className={`flex w-full items-center gap-3 px-3.5 py-2 text-left ${i === index ? 'bg-[#F7F8FB]' : ''}`}>
              {r.kind === 'user' && <Avatar name={r.label} photo={r.photo} size={32} />}
              {r.kind === 'vehicle' && <VehicleThumb vehicle={r.vehicle} size={32} radius={9} />}
              {r.kind === 'section' && (
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#F1F2F8]">
                  <LineIcon d={ICONS.chevron} size={14} color="#475569" width={2.4} />
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[13.5px] font-semibold text-[#1E293B]">{r.label}</span>
                <span className="block truncate text-[11.5px] text-[#6B7280]">
                  {r.kind === 'section' ? 'Section' : r.sub}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
